import { EVENTS, TIMEOUTS, SELECTORS } from './shared/constants.js';

/**
 * BaseRealizedPresetEffect
 * 
 * Abstract base class providing common functionality for realized preset form effects.
 * Handles AJAX requests, caching, debouncing, and common DOM manipulation utilities.
 */
class BaseRealizedPresetEffect {
  constructor() {
    this.cache = new Map(); // Cache for API responses
    this.pendingRequests = new Map(); // Track pending AJAX requests
    this.debounceTimers = new Map(); // Debounce timers for user interactions
  }

  /**
   * Generic fetch method with caching and request management
   * @param {string} url - The URL to fetch from
   * @param {Object} params - Query parameters
   * @param {string} cacheKey - Key for caching the response
   * @param {string} nodeIndex - Node index for request tracking
   * @returns {Promise} Promise that resolves with the response data
   */
  async fetchData(url, params, cacheKey, nodeIndex) {
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return Promise.resolve(this.cache.get(cacheKey));
    }

    // Cancel any existing request for this node
    this.cancelPendingRequest(nodeIndex);

    // Build URL with parameters
    const fetchUrl = new URL(url, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        fetchUrl.searchParams.append(key, params[key]);
      }
    });

    // Create abort controller for this request
    const controller = new AbortController();
    this.pendingRequests.set(nodeIndex, controller);

    try {
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache the successful response
      this.cache.set(cacheKey, data);
      
      return data;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching data:', error);
        throw error;
      }
      throw error;
    } finally {
      this.pendingRequests.delete(nodeIndex);
    }
  }

  /**
   * Cancel pending request for a specific node
   * @param {string} nodeIndex - The node index to cancel requests for
   */
  cancelPendingRequest(nodeIndex) {
    if (this.pendingRequests.has(nodeIndex)) {
      this.pendingRequests.get(nodeIndex).abort();
      this.pendingRequests.delete(nodeIndex);
    }
  }

  /**
   * Debounce a callback function
   * @param {Function} callback - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @param {string} timerId - Unique identifier for the timer
   */
  debounce(callback, delay, timerId) {
    // Clear existing timer
    if (this.debounceTimers.has(timerId)) {
      clearTimeout(this.debounceTimers.get(timerId));
    }

    // Set new timer
    this.debounceTimers.set(timerId, setTimeout(() => {
      callback();
      this.debounceTimers.delete(timerId);
    }, delay));
  }

  /**
   * Show loading state in an element
   * @param {HTMLElement} element - Element to show loading state in
   * @param {string} message - Optional loading message
   */
  showLoadingState(element, message = 'Loading...') {
    if (!element) return;

    element.innerHTML = `
      <div class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="loading-text">${message}</div>
      </div>
    `;
  }

  /**
   * Show error state in an element
   * @param {HTMLElement} element - Element to show error state in
   * @param {string} message - Optional error message
   */
  showErrorState(element, message = 'Error loading data') {
    if (!element) return;

    element.innerHTML = `
      <div class="error-state">
        <svg width="48" height="48" fill="currentColor" class="text-danger" viewBox="0 0 16 16">
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
        </svg>
        <div class="error-text">${message}</div>
      </div>
    `;
  }

  /**
   * Show placeholder state in an element
   * @param {HTMLElement} element - Element to show placeholder in
   * @param {string} message - Placeholder message
   */
  showPlaceholder(element, message = 'Select an option') {
    if (!element) return;

    element.innerHTML = `
      <div class="placeholder-state">
        <svg width="48" height="48" fill="currentColor" class="text-muted" viewBox="0 0 16 16">
          <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
          <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
        </svg>
        <div class="placeholder-text">${message}</div>
      </div>
    `;
  }

  /**
   * Find all preset node containers
   * @returns {NodeList} List of preset node container elements
   */
  getPresetNodeContainers() {
    return document.querySelectorAll('.js-preset-node-container');
  }

  /**
   * Get node data from container element
   * @param {HTMLElement} container - Container element
   * @returns {Object} Node data object
   */
  getNodeData(container) {
    return {
      type: container.dataset.nodeType,
      index: container.dataset.nodeIndex
    };
  }

  /**
   * Abstract method - must be implemented by subclasses
   * Initialize the effect functionality
   */
  initialize() {
    throw new Error('initialize() method must be implemented by subclass');
  }

  /**
   * Dispatch a custom event on a specific node container
   * @param {string} nodeIndex - The node index
   * @param {string} eventName - Name of the event to dispatch
   * @param {Object} detail - Event detail data
   */
  dispatchNodeEvent(nodeIndex, eventName, detail = {}) {
    const container = document.querySelector(`[data-node-index="${nodeIndex}"]`);
    if (container) {
      container.dispatchEvent(new CustomEvent(eventName, { 
        detail: { nodeIndex, ...detail },
        bubbles: true 
      }));
    }
  }

  /**
   * Wait for a specific event on a node container
   * @param {string} nodeIndex - The node index to listen on
   * @param {string} eventName - Name of the event to wait for
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise} Promise that resolves with event detail or rejects on timeout
   */
  waitForNodeEvent(nodeIndex, eventName, timeout = TIMEOUTS.COLOR_LOADING_MAX_WAIT) {
    return new Promise((resolve, reject) => {
      const container = document.querySelector(`[data-node-index="${nodeIndex}"]`);
      if (!container) {
        reject(new Error(`Container not found for node index: ${nodeIndex}`));
        return;
      }

      const timeoutId = setTimeout(() => {
        container.removeEventListener(eventName, handler);
        reject(new Error(`Timeout waiting for event: ${eventName}`));
      }, timeout);
      
      function handler(event) {
        clearTimeout(timeoutId);
        container.removeEventListener(eventName, handler);
        resolve(event.detail);
      }

      container.addEventListener(eventName, handler, { once: true });
    });
  }

  /**
   * Check if a node container exists
   * @param {string} nodeIndex - The node index to check
   * @returns {boolean} True if container exists
   */
  nodeExists(nodeIndex) {
    return !!document.querySelector(`[data-node-index="${nodeIndex}"]`);
  }

  /**
   * Get a node container by index
   * @param {string} nodeIndex - The node index
   * @returns {HTMLElement|null} The container element or null
   */
  getNodeContainer(nodeIndex) {
    return document.querySelector(`[data-node-index="${nodeIndex}"]`);
  }

  /**
   * Cleanup method to clear timers and abort pending requests
   */
  cleanup() {
    // Clear all debounce timers
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();

    // Abort all pending requests
    this.pendingRequests.forEach(controller => controller.abort());
    this.pendingRequests.clear();

    // Clear cache
    this.cache.clear();
  }
}

export default BaseRealizedPresetEffect;
